import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Sale Visibility Settings (特売公開設定)
export const getSaleVisibility = () => api.get('/settings/sale-visibility');
export const updateSaleVisibility = (isPublished) => api.put('/settings/sale-visibility', { is_published: isPublished });

// Products (商品マスタ)
export const getProducts = () => api.get('/products');
export const createProducts = (items) => api.post('/products', { items });
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const migrateSalesToProducts = () => api.post('/products/migrate-sales');

// Sales (特売マスタ)
export const getSales = () => api.get('/sales');
export const createSales = (items) => api.post('/sales', { items });
export const updateSale = (id, data) => api.put(`/sales/${id}`, data);
export const deleteSale = (id) => api.delete(`/sales/${id}`);

// Customers (取引先)
export const getCustomers = () => api.get('/customers');
export const getCustomersWithUsers = () => api.get('/customers-with-users');
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const getCustomerOrderHistory = (customerId) => api.get(`/customers/${customerId}/order-history`);

// Suppliers (仕入れ先)
export const getSuppliers = () => api.get('/suppliers');
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);

// Units (単位マスタ)
export const getUnits = () => api.get('/units');
export const createUnit = (data) => api.post('/units', data);
export const updateUnit = (id, data) => api.put(`/units/${id}`, data);
export const deleteUnit = (id) => api.delete(`/units/${id}`);

// Categories (カテゴリマスタ)
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// CSV Import (商品一括インポート)
export const importProductsCSV = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/products/import-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Purchase Records (仕入れ記録)
export const getPurchaseRecords = (params) => api.get('/purchase-records', { params });
export const savePurchaseRecord = (data) => api.post('/purchase-records', data);
export const getProfitStats = (date) => api.get('/profit-stats', { params: { date } });

// Payment Preparations (支払い準備)
export const getPaymentPreparation = (date) => api.get('/payment-preparations', { params: { date } });
export const savePaymentPreparation = (data) => api.post('/payment-preparations', data);
export const getCashSummary = (date) => api.get('/payment-preparations/cash-summary', { params: { date } });
export const getNextDeliveryDate = (from_date) => api.get('/next-delivery-date', { params: { from_date } });
export const getPrevDeliveryDate = (from_date) => api.get('/prev-delivery-date', { params: { from_date } });
export const getWeeklyPaymentSuppliers = () => api.get('/suppliers/weekly-payment');
export const getNextWeekCreditSummary = (date) => api.get('/payment-preparations/next-week-credit', { params: { date } });
export const getMonthlyCashCreditSummary = (date) => api.get('/payment-preparations/monthly-cash-credit', { params: { date } });
export const getRouteOrder = () => api.get('/route-order');
export const saveRouteOrder = (routes) => api.post('/route-order', { routes });
export const getAnnouncements = () => api.get('/announcements/all');
export const createAnnouncement = (message) => api.post('/announcements', { message });
export const updateAnnouncement = (id, data) => api.put(`/announcements/${id}`, data);
export const deleteAnnouncement = (id) => api.delete(`/announcements/${id}`);
export const editCreditPayment = (data) => api.put('/credit-payments/edit', data);
export const getPinCustomers = () => api.get('/customers/pin-entry');
export const getPinEntries = (month) => api.get('/pin-entries', { params: { month } });
export const createPinEntry = (data) => api.post('/pin-entries', data);
export const updatePinEntry = (id, data) => api.put(`/pin-entries/${id}`, data);
export const deletePinEntry = (id) => api.delete(`/pin-entries/${id}`);
export const markPinEntriesExported = (entryIds) => api.post('/pin-entries/mark-exported', { entry_ids: entryIds });

// Credit Payments (掛け金管理)
export const getCreditPaymentSummary = (month) => api.get('/credit-payments/summary', { params: { month } });

// Tewari Box (手割りボックス)
export const getTewariBox = (date) => api.get('/tewari-box', { params: { date } });
export const saveTewariDecisions = (data) => api.post('/tewari-box', data);
export const getTewariUndecidedCount = () => api.get('/tewari-box/undecided-count');

// Order Dates (発注可能日)
export const getOrderDates = () => api.get('/order-dates');
export const createOrderDates = (dates) => api.post('/order-dates', { dates });
export const deleteOrderDate = (date) => api.delete(`/order-dates/${date}`);

// Coupons (クーポン)
export const getCoupons = () => api.get('/coupons');
export const createCoupon = (data) => api.post('/coupons', data);
export const updateCoupon = (id, data) => api.put(`/coupons/${id}`, data);
export const deleteCoupon = (id) => api.delete(`/coupons/${id}`);

// Orders
export const getOrders = () => api.get('/orders');
export const getOrder = (id) => api.get(`/orders/${id}`);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const getOrdersBySupplier = (supplierId, date) => 
  api.get(`/orders/by-supplier/${supplierId}`, { params: { date } });

// File Upload
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.url;
};

// Alias for uploadFile
export const uploadImage = uploadFile;

// Quote Requests (見積もり依頼)
export const getQuoteRequests = () => api.get('/quote-requests');
export const updateQuoteRequestStatus = (id, status) => api.put(`/quote-requests/${id}?status=${status}`);
export const getPendingQuoteRequestsCount = () => api.get('/quote-requests/count');

export default api;
